/**
 * Fractions written as Unicode text: the vulgar fractions Unicode encodes as single
 * characters (½ ¾ ⅓ ⅝…) and, for every other fraction, a superscript numerator, the
 * fraction slash (U+2044) and a subscript denominator — ¹⁷⁄₂₄.
 *
 * ## What Unicode provides
 *
 * - Precomposed vulgar fractions: ¼ ½ ¾ (U+00BC–U+00BE, from Latin-1), ⅐ to ⅞
 *   (U+2150–U+215E), and ↉ (0/3, U+2189, kept for compatibility with Japanese baseball
 *   scoring). All are listed in {@link VULGAR_FRACTIONS}; the test suite checks every
 *   name against the Unicode Character Database.
 * - ⅟ (U+215F, FRACTION NUMERATOR ONE) followed by subscript digits writes any unit
 *   fraction: ⅟₂₄.
 * - The superscript digits ⁰¹²³⁴⁵⁶⁷⁸⁹ (U+2070, U+00B9, U+00B2, U+00B3, U+2074–U+2079)
 *   and subscript digits ₀–₉ (U+2080–U+2089).
 * - The fraction slash U+2044. Fonts with the `frac` feature, and many without, draw a
 *   run of digits around it as one fraction; where that fails, the ordinary slash is a
 *   safer separator, so the tool offers both.
 *
 * Nothing here is rendering: it only chooses which characters to emit.
 */

export const FRACTION_SLASH = "⁄";
export const MINUS = "−";

const SUPERSCRIPT_DIGITS = [
  "⁰",
  "¹",
  "²",
  "³",
  "⁴",
  "⁵",
  "⁶",
  "⁷",
  "⁸",
  "⁹",
] as const;
const SUBSCRIPT_DIGITS = [
  "₀",
  "₁",
  "₂",
  "₃",
  "₄",
  "₅",
  "₆",
  "₇",
  "₈",
  "₉",
] as const;

export interface VulgarFraction {
  char: string;
  numerator: number;
  denominator: number;
  /** The Unicode character name, for the reference table and the tests. */
  name: string;
}

export const VULGAR_FRACTIONS: readonly VulgarFraction[] = [
  {
    char: "¼",
    numerator: 1,
    denominator: 4,
    name: "VULGAR FRACTION ONE QUARTER",
  },
  { char: "½", numerator: 1, denominator: 2, name: "VULGAR FRACTION ONE HALF" },
  {
    char: "¾",
    numerator: 3,
    denominator: 4,
    name: "VULGAR FRACTION THREE QUARTERS",
  },
  {
    char: "⅐",
    numerator: 1,
    denominator: 7,
    name: "VULGAR FRACTION ONE SEVENTH",
  },
  {
    char: "⅑",
    numerator: 1,
    denominator: 9,
    name: "VULGAR FRACTION ONE NINTH",
  },
  {
    char: "⅒",
    numerator: 1,
    denominator: 10,
    name: "VULGAR FRACTION ONE TENTH",
  },
  {
    char: "⅓",
    numerator: 1,
    denominator: 3,
    name: "VULGAR FRACTION ONE THIRD",
  },
  {
    char: "⅔",
    numerator: 2,
    denominator: 3,
    name: "VULGAR FRACTION TWO THIRDS",
  },
  {
    char: "⅕",
    numerator: 1,
    denominator: 5,
    name: "VULGAR FRACTION ONE FIFTH",
  },
  {
    char: "⅖",
    numerator: 2,
    denominator: 5,
    name: "VULGAR FRACTION TWO FIFTHS",
  },
  {
    char: "⅗",
    numerator: 3,
    denominator: 5,
    name: "VULGAR FRACTION THREE FIFTHS",
  },
  {
    char: "⅘",
    numerator: 4,
    denominator: 5,
    name: "VULGAR FRACTION FOUR FIFTHS",
  },
  {
    char: "⅙",
    numerator: 1,
    denominator: 6,
    name: "VULGAR FRACTION ONE SIXTH",
  },
  {
    char: "⅚",
    numerator: 5,
    denominator: 6,
    name: "VULGAR FRACTION FIVE SIXTHS",
  },
  {
    char: "⅛",
    numerator: 1,
    denominator: 8,
    name: "VULGAR FRACTION ONE EIGHTH",
  },
  {
    char: "⅜",
    numerator: 3,
    denominator: 8,
    name: "VULGAR FRACTION THREE EIGHTHS",
  },
  {
    char: "⅝",
    numerator: 5,
    denominator: 8,
    name: "VULGAR FRACTION FIVE EIGHTHS",
  },
  {
    char: "⅞",
    numerator: 7,
    denominator: 8,
    name: "VULGAR FRACTION SEVEN EIGHTHS",
  },
  {
    char: "↉",
    numerator: 0,
    denominator: 3,
    name: "VULGAR FRACTION ZERO THIRDS",
  },
];

export const NUMERATOR_ONE = "⅟";
export const NUMERATOR_ONE_NAME = "FRACTION NUMERATOR ONE";

const PRECOMPOSED = new Map(
  VULGAR_FRACTIONS.map((fraction) => [
    `${fraction.numerator}/${fraction.denominator}`,
    fraction.char,
  ])
);

export function toSuperscript(digits: string): string {
  return Array.from(digits, (d) => SUPERSCRIPT_DIGITS[Number(d)] ?? d).join("");
}

export function toSubscript(digits: string): string {
  return Array.from(digits, (d) => SUBSCRIPT_DIGITS[Number(d)] ?? d).join("");
}

/* ------------------------------------------------------------------ parsing */

export interface ParsedFraction {
  negative: boolean;
  /** The whole-number part of a mixed number, or 0. */
  whole: number;
  hasWhole: boolean;
  numerator: number;
  denominator: number;
}

export type ParseProblem =
  | "empty"
  | "unreadable"
  | "zeroDenominator"
  | "tooLarge";

const MAX_DIGITS = 9;
/** `[-] [whole ] numerator / denominator`, with any common minus or slash. */
const FRACTION_LINE = /^([-+−])?\s*(?:(\d+)\s+)?(\d+)\s*[/⁄∕]\s*(\d+)$/;

export function parseFractionLine(
  line: string
): { fraction: ParsedFraction } | { problem: ParseProblem } {
  const text = line.trim();
  if (text === "") {
    return { problem: "empty" };
  }
  const match = FRACTION_LINE.exec(text);
  if (!match) {
    return { problem: "unreadable" };
  }
  const [, sign, whole, top, bottom] = match;
  if (
    (whole ?? "").length > MAX_DIGITS ||
    (top as string).length > MAX_DIGITS ||
    (bottom as string).length > MAX_DIGITS
  ) {
    return { problem: "tooLarge" };
  }
  const denominator = Number(bottom);
  if (denominator === 0) {
    return { problem: "zeroDenominator" };
  }
  return {
    fraction: {
      negative: sign === "-" || sign === MINUS,
      whole: Number(whole ?? 0),
      hasWhole: whole !== undefined,
      numerator: Number(top),
      denominator,
    },
  };
}

/* --------------------------------------------------------------- formatting */

export type FractionStyle = "auto" | "built";
export type SlashChoice = "fraction" | "solidus";

export interface FormatOptions {
  /** `auto` uses a single-character fraction where Unicode has one. */
  style: FractionStyle;
  slash: SlashChoice;
  reduce: boolean;
  /** Write an improper fraction such as 7/4 as 1¾. */
  mixed: boolean;
}

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

function builtFraction(
  numerator: number,
  denominator: number,
  slash: SlashChoice
): string {
  return `${toSuperscript(String(numerator))}${
    slash === "fraction" ? FRACTION_SLASH : "/"
  }${toSubscript(String(denominator))}`;
}

/** The fraction part alone: a precomposed character, ⅟₂₄ style, or a built fraction. */
function fractionText(
  numerator: number,
  denominator: number,
  options: FormatOptions
): { text: string; precomposed: boolean } {
  if (options.style === "auto") {
    const single = PRECOMPOSED.get(`${numerator}/${denominator}`);
    if (single) {
      return { text: single, precomposed: true };
    }
    if (numerator === 1 && options.slash === "fraction") {
      return {
        text: NUMERATOR_ONE + toSubscript(String(denominator)),
        precomposed: false,
      };
    }
  }
  return {
    text: builtFraction(numerator, denominator, options.slash),
    precomposed: false,
  };
}

/**
 * One parsed fraction as Unicode text.
 *
 * With `reduce`, the fraction goes to lowest terms and a result with denominator 1 is
 * just a whole number. With `mixed`, an improper fraction becomes a whole number and a
 * proper fraction. A whole number is joined directly to a single-character fraction
 * (1½) and separated by a space from a built one, because digits run straight into
 * superscripts would read as an exponent.
 */
export function formatFraction(
  parsed: ParsedFraction,
  options: FormatOptions
): string {
  let { numerator, denominator, whole } = parsed;
  let hasWhole = parsed.hasWhole;
  if (options.reduce) {
    const divisor = gcd(numerator, denominator) || 1;
    numerator /= divisor;
    denominator /= divisor;
  }
  if (options.mixed && numerator >= denominator) {
    whole += Math.floor(numerator / denominator);
    numerator %= denominator;
    hasWhole = true;
  }
  const sign = parsed.negative ? MINUS : "";
  if (numerator === 0 && hasWhole) {
    return `${sign}${whole}`;
  }
  if (options.reduce && denominator === 1 && !hasWhole) {
    return `${sign}${numerator}`;
  }
  const part = fractionText(numerator, denominator, options);
  if (!hasWhole) {
    return `${sign}${part.text}`;
  }
  return `${sign}${whole}${part.precomposed ? "" : " "}${part.text}`;
}
